import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {NgIf} from "@angular/common";
import {Subscription} from "rxjs";

@Component({
  selector: 'app-filter-range',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIf
  ],
  templateUrl: './filter-range.component.html',
  styleUrl: './filter-range.component.scss'
})
export class FilterRangeComponent implements OnInit, OnDestroy {

  @Input() control!: FormControl;
  @Input() required: boolean = false;
  @Input() placeholder: string = '';
  @Input() showlabel: boolean = true;
  @Input() prefix: string = '';
  @Input() suffix: string = '';
  @Input() max: number | null = null;

  minDisplayValue: string = '';
  maxDisplayValue: string = '';

  minMaxForm: FormGroup;
  private valueChanges: Subscription;

  constructor(private fb: FormBuilder) {
    this.minMaxForm = this.fb.group({
      min: [null],
      max: [null]
    });
  }

  ngOnInit() {
    // Initialize form from existing control value
    this.syncFromControlValue();

    // Subscribe to form changes
    this.valueChanges = this.minMaxForm.valueChanges.subscribe(value => {
      if (this.control) {
        // Convert values to numbers and handle nulls
        const min = value.min !== null && value.min !== '' ? Number(value.min) : null;
        const max = value.max !== null && value.max !== '' ? Number(value.max) : null;

        // Only update if at least one value is not null
        if (min !== null || max !== null) {
          this.control.setValue([min, max]);
          this.control.markAsDirty();
        }
      }
    });

    // Also subscribe to control changes from outside
    if (this.control) {
      this.control.valueChanges.subscribe(value => {
        // Only update the form if the value is different to avoid loops
        if (value && Array.isArray(value) &&
          (value[0] !== this.minMaxForm.get('min')?.value ||
            value[1] !== this.minMaxForm.get('max')?.value)) {
          this.syncFromControlValue();
        }
      });
    }
  }

  private syncFromControlValue() {
    if (this.control && this.control.value) {
      const value = this.control.value;

      // Handle array values
      if (Array.isArray(value) && value.length === 2) {
        // Prevent valueChanges from firing during initialization
        this.minMaxForm.setValue({
          min: value[0] !== null ? value[0] : null,
          max: value[1] !== null ? value[1] : null
        }, { emitEvent: false });

        this.minDisplayValue = value[0] !== null ? this.formatNumber(value[0]) : '';
        this.maxDisplayValue = value[1] !== null ? this.formatNumber(value[1]) : '';
      }
    }
  }

  formatNumber(value: number | string): string {
    if (value === null || value === undefined || value === '') return '';
    const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US');
  }

  onFormattedInput(field: 'min' | 'max', event: Event) {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/,/g, '');
    let num = raw === '' ? null : Number(raw);

    if (raw !== '' && isNaN(num as number)) return;

    // Clamp to the configured upper bound (used e.g. by Company Estimated Revenue
    // to stay under Veridion's empirical max of ~1,000,000,000,000).
    if (num !== null && this.max != null && num > this.max) {
      num = this.max;
    }

    if (field === 'min') {
      this.minDisplayValue = num !== null ? this.formatNumber(num) : '';
    } else {
      this.maxDisplayValue = num !== null ? this.formatNumber(num) : '';
    }

    this.minMaxForm.get(field)?.setValue(num, { emitEvent: true });

    // Restore cursor position after formatting
    const cursorPos = input.selectionStart || 0;
    const oldLength = input.value.length;
    input.value = field === 'min' ? this.minDisplayValue : this.maxDisplayValue;
    const newLength = input.value.length;
    const newCursorPos = Math.max(0, cursorPos + (newLength - oldLength));
    input.setSelectionRange(newCursorPos, newCursorPos);
  }

  public reset(): void {
    this.minMaxForm.reset();
    this.minDisplayValue = '';
    this.maxDisplayValue = '';
    if (this.control) {
      this.control.setValue(null);
      this.control.markAsPristine();
      this.control.markAsUntouched();
    }
  }

  ngOnDestroy() {
    // Clean up subscriptions
    if (this.valueChanges) {
      this.valueChanges.unsubscribe();
    }
  }

}
